export enum GitHubRepositorySelection {
  ALL = 'all',
  SELECTED = 'selected',
}

export interface GitHubRepositoryReference {
  id: number;
  fullName: string;
}

// GitHub's own grammar, not merely "has a slash": an owner is alphanumeric with
// hyphens (<=39), a repo adds dot and underscore (<=100). A looser check still
// admits names GitHub can never issue — `acme/api.git` being the common paste —
// which persists a filter nothing can ever match.
const REPOSITORY_FULL_NAME_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9._-]{1,100}$/;

const GIT_SUFFIX = '.git';

/** Trims and drops a clone-URL `.git` suffix so a pasted name still validates. */
export const normalizeRepositoryFullName = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.endsWith(GIT_SUFFIX)
    ? trimmed.slice(0, -GIT_SUFFIX.length)
    : trimmed;
};

export const isValidRepositoryFullName = (value: unknown): value is string =>
  typeof value === 'string' &&
  REPOSITORY_FULL_NAME_PATTERN.test(normalizeRepositoryFullName(value));

export interface GitHubInstallationKeys {
  teamId: string; // PK
  installationId: number; // SK — a team may hold several installations
}

export interface GitHubInstallationFields {
  accountId: number; // immutable org/user id — source of truth
  accountLogin: string; // display only; mutable
  repositorySelection: GitHubRepositorySelection;
  repositories: GitHubRepositoryReference[];
  connectedAt: number;
  // Transient soft-disconnect marker: set true immediately before the delete so the
  // table-stream purge preserves the team's UserCommunications instead of deleting them.
  shouldPreserveData?: boolean;
}

export interface GitHubInstallationItem
  extends GitHubInstallationKeys,
    GitHubInstallationFields {}

export const GITHUB_WEBHOOK_EVENT = {
  PULL_REQUEST: 'pull_request',
  PULL_REQUEST_REVIEW: 'pull_request_review',
  ISSUES: 'issues',
  ISSUE_COMMENT: 'issue_comment',
  RELEASE: 'release',
  PUSH: 'push',
  INSTALLATION: 'installation',
  INSTALLATION_REPOSITORIES: 'installation_repositories',
  PING: 'ping',
} as const;

export type GitHubWebhookEvent =
  (typeof GITHUB_WEBHOOK_EVENT)[keyof typeof GITHUB_WEBHOOK_EVENT];

export const GITHUB_ACTION = {
  OPENED: 'opened',
  REOPENED: 'reopened',
  CLOSED: 'closed',
  // Synthetic: GitHub has no `merged` action — it sends `closed` with
  // pull_request.merged true. Derived in handleContentEvent so a flow can
  // filter a landed PR apart from an abandoned one.
  MERGED: 'merged',
  ASSIGNED: 'assigned',
  REVIEW_REQUESTED: 'review_requested',
  SUBMITTED: 'submitted',
  CREATED: 'created',
  DELETED: 'deleted',
  PUBLISHED: 'published',
  // Synthetic: `push` carries no action at all.
  PUSHED: 'pushed',
} as const;

export type GitHubAction = (typeof GITHUB_ACTION)[keyof typeof GITHUB_ACTION];

export const GITHUB_EVENT_ACTIONS: Partial<
  Record<GitHubWebhookEvent, readonly GitHubAction[]>
> = {
  [GITHUB_WEBHOOK_EVENT.PULL_REQUEST]: [
    GITHUB_ACTION.OPENED,
    GITHUB_ACTION.REOPENED,
    GITHUB_ACTION.CLOSED,
    GITHUB_ACTION.MERGED,
    GITHUB_ACTION.REVIEW_REQUESTED,
  ],
  [GITHUB_WEBHOOK_EVENT.ISSUES]: [
    GITHUB_ACTION.OPENED,
    GITHUB_ACTION.REOPENED,
    GITHUB_ACTION.CLOSED,
    GITHUB_ACTION.ASSIGNED,
  ],
  [GITHUB_WEBHOOK_EVENT.ISSUE_COMMENT]: [GITHUB_ACTION.CREATED],
  [GITHUB_WEBHOOK_EVENT.PULL_REQUEST_REVIEW]: [GITHUB_ACTION.SUBMITTED],
  // `published` only: GitHub fires `released`/`prereleased` for the same publish,
  // so allowlisting them too would create three rows for one release.
  [GITHUB_WEBHOOK_EVENT.RELEASE]: [GITHUB_ACTION.PUBLISHED],
  // `pushed` only: branch/tag creation and deletion also arrive as `push`, but
  // belong to the `create`/`delete` events, which this integration does not use.
  [GITHUB_WEBHOOK_EVENT.PUSH]: [GITHUB_ACTION.PUSHED],
};

export interface GitHubUserIdentity {
  installationId: number; // PK — lets the disconnect purge delete an install's cache
  githubUserId: number; // SK — stable across GitHub username renames
  login: string;
  email: string; // '' = negative-cache sentinel (no public email)
  updatedAt: number; // epoch ms
  ttl: number; // epoch SECONDS (DynamoDB TTL)
}

export const GITHUB_SETUP_ACTION = {
  INSTALL: 'install',
  UPDATE: 'update',
  // An org member without install rights picked the org: GitHub created an
  // installation request for an owner to approve and sent us back WITHOUT an
  // installation_id, because no installation exists yet.
  REQUEST: 'request',
} as const;

export type GitHubSetupAction =
  (typeof GITHUB_SETUP_ACTION)[keyof typeof GITHUB_SETUP_ACTION];

// Bridges the two halves of the org-approval flow: the requester's browser knows the
// teamId but never sees an installationId, and the owner's approval arrives as an
// installation.created webhook that carries no state — only `requester.id` and the
// target account. Both together are the key, so one requester can have a pending
// request per organization without either overwriting the other.
export interface GitHubInstallationRequestItem {
  githubUserId: number; // PK
  accountId: number; // SK
  accountLogin: string;
  teamId: string;
  userId: string;
  login: string;
  requestedAt: number;
  // epoch SECONDS (DynamoDB TTL). GitHub fires no webhook when an owner DENIES a
  // request, so expiry is the only thing that clears an unanswered one.
  ttl: number;
}

export interface GitHubInstallationRequestKeys {
  githubUserId: number;
  accountId: number;
}

// teamId is NOT accepted from the caller: it is read out of the signed state, so a
// forged callback cannot bind a request to someone else's team.
export interface GitHubInstallationRequestBody {
  state: string;
  code: string;
}

export interface GitHubInstallationRequestResponse {
  message: string;
  login: string;
  accountLogin: string;
  requestedAt: number;
}

export interface GitHubInstallationRequestView {
  login: string;
  accountLogin: string;
  requestedAt: number;
}

export interface GitHubInitialSyncRequest {
  installationId: string;
  code: string;
  state: string;
}

export interface GitHubInitialSyncResponse {
  message: string;
  accountLogin: string;
  repositoryCount: number;
}

export interface GitHubUnsubscribeRequest {
  teamId: string;
  installationId: string;
  // false/absent → preserve the team's GitHub UserCommunications; true → purge them.
  deleteData?: boolean;
}

export interface GitHubIntegrationView {
  installationId: number;
  accountLogin: string;
  repositorySelection: GitHubRepositorySelection;
  // Repository fullNames. Populated for BOTH selection modes — fetchInstallationDetail
  // paginates listReposAccessibleToInstallation regardless. For ALL it is a snapshot
  // taken at connect: GitHub fires no installation_repositories webhook for that mode,
  // so a repo created later is absent until the installation is reconnected.
  repositories: string[];
  teamId: string;
  connectedAt: number;
}
